import penguinBlack from '@/assets/logos/penguin-mark.png';
import penguinEggshell from '@/assets/logos/penguin-mark-eggshell.png';
import { cn } from '@/lib/cn';

type LogoVariant = 'full' | 'mark';
type LogoTone = 'black' | 'white';
type LogoSize = 'sm' | 'md' | 'lg';

type LogoProps = {
    variant?: LogoVariant;
    tone?: LogoTone;
    /** Only meaningful for `variant="full"`. The mark variant is sized via className. */
    size?: LogoSize;
    className?: string;
    alt?: string;
};

const MARK: Record<LogoTone, string> = {
    black: penguinBlack,
    white: penguinEggshell,
};

const FULL_SIZES: Record<LogoSize, { mark: string; name: string; tag: string; gap: string }> = {
    sm: { mark: 'h-8 w-8',   name: 'text-lg',  tag: 'text-[8px] tracking-[0.22em]',  gap: 'gap-2' },
    md: { mark: 'h-12 w-12', name: 'text-2xl', tag: 'text-[10px] tracking-[0.24em]', gap: 'gap-2.5' },
    lg: { mark: 'h-16 w-16', name: 'text-3xl', tag: 'text-[11px] tracking-[0.26em]', gap: 'gap-3' },
};

export function Logo({
    variant = 'full',
    tone = 'black',
    size = 'md',
    className,
    alt = 'Good Steward — Practice Management',
}: LogoProps) {
    if (variant === 'mark') {
        return (
            <img
                src={MARK[tone]}
                alt={alt}
                className={cn('select-none', className)}
                draggable={false}
            />
        );
    }

    const s = FULL_SIZES[size];
    const wordmarkColor = tone === 'white' ? 'text-text-inverse' : 'text-text';
    const tagColor = tone === 'white' ? 'text-sidebar-text-subtle' : 'text-text-muted';

    return (
        <div
            role="img"
            aria-label={alt}
            className={cn('flex items-center select-none', s.gap, className)}
        >
            <img
                src={MARK[tone]}
                alt=""
                aria-hidden="true"
                className={cn('shrink-0', s.mark)}
                draggable={false}
            />
            <div className="flex flex-col leading-none min-w-0">
                <span className={cn('font-brand tracking-tight whitespace-nowrap', s.name, wordmarkColor)}>
                    Good Steward
                </span>
                <span
                    className={cn(
                        'font-sans font-medium uppercase whitespace-nowrap mt-1',
                        s.tag,
                        tagColor,
                    )}
                >
                    Practice Management
                </span>
            </div>
        </div>
    );
}
